import {
  UploadResponse,
  RunDefaultsResponse,
} from "@/services/medicalReportProtocolService";
import { useCallback, useEffect, useState } from "react";
import MedicalReportPromptType from "@/data/interfaces/MedicalReportPromptType";
import MedicalReportUploadForm from "./MedicalReportUploadForm";
import ClarificationForm from "./ClarificationForm";
import { ErrorMessage } from "@/components/UI";
import { Button } from "@/components/UI/Buttons/Button";
import medicalReportProtocolService from "@/services/medicalReportProtocolService";

const MedicalReportPdf = () => {
  const [promptTypes, setPromptTypes] = useState<
    MedicalReportPromptType[] | undefined
  >();
  const [isPromptTypesLoading, setIsPromptTypesLoading] = useState(false);
  const [promptTypesErrorMsg, setPromptTypesErrorMsg] = useState<string>();

  const [errorMsg, setErrorMsg] = useState<string>();

  const [uploadResponses, setUploadResponses] = useState<UploadResponse[]>([]);

  const [selections, setSelections] = useState<
    Map<string, { answer: string; isClarified: boolean }>
  >(new Map());
  const [refinedResponses, setRefinedResponses] = useState<
    RunDefaultsResponse[]
  >([]);
  const [isRunningDefaults, setIsRunningDefaults] = useState(false);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const loadPromptTypes = async () => {
    try {
      setIsPromptTypesLoading(true);
      setPromptTypesErrorMsg(undefined);

      const { data } = await medicalReportProtocolService.getPromptTypes({
        per_page: 100,
        is_active: true,
        with_prompts: true,
        is_default: false,
      });
      setPromptTypes(data);
    } catch (error) {
      console.error(error);
      setPromptTypesErrorMsg(`FAILED TO LOAD PROMPT TYPES`);
    } finally {
      setIsPromptTypesLoading(false);
    }
  };

  const handleUploadSubmit = (responses: Map<number, UploadResponse>) => {
    setUploadResponses(Array.from(responses.values()));
    setRefinedResponses([]);
    setSelections(new Map());
  };

  const handleSelectionChange = useCallback(
    (key: string, answer: string, isClarified: boolean) => {
      setSelections((prev) => new Map(prev).set(key, { answer, isClarified }));
    },
    [],
  );

  const handleSubmitDefaults = async () => {
    setIsRunningDefaults(true);
    const refined: RunDefaultsResponse[] = [];

    try {
      for (const uploadResponse of uploadResponses) {
        const results = uploadResponse.results.map((result) => {
          const selection = selections.get(result.key);
          return {
            key: result.key,
            answer: selection?.answer || result.answer,
            meta: result.meta,
            clarify: selection?.isClarified || false,
          };
        });

        if (results.length === 0) {
          continue;
        }

        const response = await medicalReportProtocolService.runDefaults({
          upload_id: uploadResponse.upload_id,
          results,
        });

        if (response.refined && response.refined.length > 0) {
          refined.push(response);
        } else {
          console.log("Skipping response - no refined data:", response);
        }
      }

      if (refined.length === 0) {
        setErrorMsg(
          "No default prompts were generated. This may be expected if no default prompts are configured.",
        );
      } else {
        setRefinedResponses(refined);
      }
    } catch (error) {
      console.error(error);
      setErrorMsg(`RUN DEFAULTS FAILED`);
    } finally {
      setIsRunningDefaults(false);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setErrorMsg(undefined);

    try {
      const payload = {
        upload_responses: uploadResponses,
        selections: Array.from(selections.entries()).map(([key, value]) => ({
          key,
          ...value,
        })),
        refined_responses: refinedResponses,
      };

      await medicalReportProtocolService.submit(payload);
      setSubmitSuccess(true);
    } catch (error) {
      console.error(error);
      setErrorMsg(`SUBMIT FAILED`);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    loadPromptTypes();
  }, []);

  if (isPromptTypesLoading) {
    return <p className="text-center">Loading...</p>;
  }

  if (promptTypesErrorMsg) {
    return <ErrorMessage error={promptTypesErrorMsg} />;
  }

  if (!promptTypes || promptTypes.length === 0) {
    return <p className="text-center">No prompt types</p>;
  }

  return (
    <div className="flex flex-col gap-8">
      <MedicalReportUploadForm
        types={promptTypes}
        onSubmit={handleUploadSubmit}
      />

      {uploadResponses.length > 0 && (
        <div>
          <p className="mb-4 text-2xl font-bold">Review Responses</p>

          {uploadResponses.some((r) => r.results.length > 0) ? (
            <>
              <div className="flex flex-col gap-8">
                {uploadResponses.map((response) => (
                  <div
                    key={response.upload_id}
                    className="flex flex-col gap-4 rounded-md border p-4"
                  >
                    <p className="text-xl font-semibold">{response.filename}</p>

                    {response.results.length > 0 ? (
                      response.results.map((result) => (
                        <ClarificationForm
                          key={result.key}
                          promptKey={result.key}
                          promptContent={result.prompt}
                          originalAnswer={result.answer}
                          uploadId={response.upload_id}
                          conversationId={response.conversation_id}
                          onSelectionChange={handleSelectionChange}
                        />
                      ))
                    ) : (
                      <p className="text-sm text-gray-500 italic">
                        No prompts configured for this report type
                      </p>
                    )}
                  </div>
                ))}
              </div>

              <Button
                onClick={handleSubmitDefaults}
                disabled={isRunningDefaults}
                className="mt-4"
              >
                {isRunningDefaults ? "Running Defaults..." : "Submit Defaults"}
              </Button>
            </>
          ) : (
            <p className="rounded-md border p-4 text-center text-gray-500">
              No prompts configured for the selected report types.
            </p>
          )}
        </div>
      )}

      {refinedResponses.length > 0 && (
        <div>
          <p className="mb-4 text-2xl font-bold">Review Default Prompts</p>

          <div className="flex flex-col gap-8">
            {refinedResponses.map((refinedResponse) => (
              <div key={refinedResponse.stage} className="space-y-4">
                {refinedResponse.refined?.map((item, idx) => (
                  <div
                    key={idx}
                    className="flex flex-col gap-4 rounded-md border p-4"
                  >
                    <p className="text-lg font-semibold">
                      {item.file.filename}
                    </p>

                    {item.by_default_prompts?.map((prompt) => (
                      <ClarificationForm
                        key={prompt.key}
                        promptKey={prompt.key}
                        promptContent={prompt.prompt}
                        originalAnswer={prompt.answer}
                        uploadId={item.conversation_id}
                        conversationId={item.conversation_id}
                        onSelectionChange={handleSelectionChange}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>

          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="mt-4"
          >
            {isSubmitting ? "Submitting..." : "Submit Final Results"}
          </Button>

          {submitSuccess && (
            <p className="mt-4 font-semibold text-green-600">SUCCESS</p>
          )}
        </div>
      )}

      <ErrorMessage error={errorMsg} />
    </div>
  );
};

export default MedicalReportPdf;
